// scanCertificate.js
// ─────────────────────────────────────────────────────────────────────────────
// Phase 4 — read a contractor's monthly submission (payment claim / measurement
// sheet) and extract each pay item's THIS-PERIOD quantity, so the certificate's
// monthly valuation can be pre-filled instead of hand-keyed.
//
// Contractor formats VARY (Excel, PDF, scanned photo), so this is AI-driven: the
// file goes to Claude via the app's secured anthropicProxy (same pattern as
// aiScan.js) and comes back as structured item→quantity rows. Excel/CSV is turned
// into CSV text first; PDFs/images go as document/image blocks.
//
// Like all the app's AI capture, this EXTRACTS ONLY — the engineer reviews the
// pre-filled valuation and the certificate is flagged "awaiting approval" until
// they approve it. Nothing is trusted blind: a certificate is a contract record.
// ─────────────────────────────────────────────────────────────────────────────

import { appCheckHeader } from "./appCheck";

const AI_PROXY_URL = (process.env.REACT_APP_AI_PROXY_URL || "").trim();

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = () => reject(new Error("Could not read the file."));
    r.readAsDataURL(file);
  });
}

function stripFences(s) {
  return String(s).replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
}

const isPdf = (file) => file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
const isImage = (file) => /^image\//.test(file.type || "") || /\.(png|jpe?g|gif|webp|heic)$/i.test(file.name || "");
const isSpreadsheet = (file) => /\.(xlsx?|csv|tsv)$/i.test(file.name || "") || /spreadsheet|excel|csv/i.test(file.type || "");

// Excel/CSV → CSV text of the first meaningful sheet (bounded so we never send a
// runaway payload). Uses the xlsx lib already bundled with the app.
async function spreadsheetToText(file) {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const csv = XLSX.utils.sheet_to_csv(wb.Sheets[sheetName], { blankrows: false });
  // Cap at ~200k chars — huge BoQs still fit; protects the proxy body limit.
  return csv.length > 200000 ? csv.slice(0, 200000) : csv;
}

const SYSTEM =
  "You read a construction contractor's monthly payment claim or measurement " +
  "sheet and extract, for each Bill of Quantities / Schedule pay item, the " +
  "quantity being claimed FOR THIS PERIOD (this month), not the cumulative-to-date " +
  "quantity. Sheets vary in layout; find the item-number column and the current/this-" +
  "month quantity column. Ignore subtotal, section-header and total rows. If a sheet " +
  "shows only cumulative and previous quantities, return the difference as the this-" +
  "period quantity. Respond with ONLY a JSON object — no markdown fences, no commentary.";

function buildUserText() {
  return (
`Extract the pay items claimed this period as JSON of the form:
{
  "periodMonth": "the claim's month/period if stated, e.g. \\"March 2026\\" (else \\"\\")",
  "items": [ { "itemNo": "the pay-item number exactly as written", "description": "short item description", "quantityThisMonth": <number> } ]
}

Rules:
- quantityThisMonth is a plain number (this period only). If only cumulative and
  previous are given, quantityThisMonth = cumulative − previous.
- Skip any row without a real item number or with a zero/blank this-period quantity.
- Do not invent items or quantities. Return ONLY the JSON object.`
  );
}

async function idToken() {
  try {
    const { getAuth } = await import("firebase/auth");
    const u = getAuth().currentUser;
    if (u) return await u.getIdToken();
  } catch (_) { /* falls through */ }
  return "";
}

// Read a contractor submission → { periodMonth, items: [{ itemNo, description, quantityThisMonth }] }
export async function scanContractorSubmission(file) {
  if (!AI_PROXY_URL) throw new Error("AI is not configured (REACT_APP_AI_PROXY_URL is unset).");
  if (!file) throw new Error("No file selected.");

  // Build the user content: a document/image block, or CSV text for spreadsheets.
  const content = [];
  if (isSpreadsheet(file)) {
    const text = await spreadsheetToText(file);
    content.push({ type: "text", text: `Contractor submission (spreadsheet exported to CSV):\n\n${text}` });
  } else if (isPdf(file)) {
    content.push({ type: "document", source: { type: "base64", media_type: "application/pdf", data: await fileToBase64(file) } });
  } else if (isImage(file)) {
    content.push({ type: "image", source: { type: "base64", media_type: file.type || "image/jpeg", data: await fileToBase64(file) } });
  } else {
    throw new Error("Unsupported file. Upload an Excel/CSV, PDF, or a photo/scan.");
  }
  content.push({ type: "text", text: buildUserText() });

  const token = await idToken();
  if (!token) throw new Error("You must be signed in to read a submission.");

  const res = await fetch(AI_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 4000,
      system: SYSTEM,
      messages: [{ role: "user", content }],
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error?.message || data?.error || `Read failed (${res.status}).`);

  const text = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
  let parsed;
  try {
    parsed = JSON.parse(stripFences(text));
  } catch (_) {
    throw new Error("The submission was read but the result couldn't be parsed. Try a clearer copy.");
  }

  const items = Array.isArray(parsed?.items) ? parsed.items : [];
  const cleaned = items
    .map((it) => ({
      itemNo: String(it.itemNo ?? "").trim(),
      description: String(it.description ?? "").trim(),
      quantityThisMonth: Number(String(it.quantityThisMonth ?? "").replace(/[^0-9.\-]/g, "")),
    }))
    .filter((it) => it.itemNo && Number.isFinite(it.quantityThisMonth) && it.quantityThisMonth !== 0);

  return { periodMonth: String(parsed?.periodMonth ?? "").trim(), items: cleaned };
}
